import React, { useState, useEffect } from 'react';
import { IoArrowRedoOutline } from 'react-icons/io5';
import { XIcon } from '@heroicons/react/solid';
import { FaLock } from 'react-icons/fa';
import { useRouter } from 'next/router';

import Link from 'components/Link';
import PulseLoader from 'components/Loading/Pulse';
import Modal from 'components/Modal/HeadlessUiModal';

import useViewer from 'hooks/useViewer';

import { AuthStatus } from 'constants/auth';
import { LOGIN_PAGE, SIGNUP_PAGE } from 'constants/pages';
import {
  useInsertOpcSavedStrategyMutation,
  InsertOpcSavedStrategyMutationVariables,
} from 'types/generated/client';
import { StrategyBuilder } from 'utils/shared/quantlib/StrategyBuilder';
import {
  calculateAggregateContractValueOfOptionPosition,
  calculateUnderlyingStockPositionMetrics,
} from 'utils/shared/quantlib/BlackScholes';

import { StockOptionParamters, UnderlyingStockParameters, SaveStrategyModalProps } from './types';

const prepareDataForSavingStrategy = ({
  userId,
  stockPrice,
  stockOptions,
  underlyingStocks,
  ticker,
  riskFreeRate,
  title,
  description,
  normalizedProfitFactor: profitIndex,
  netCreditDebit,
  impliedVolatility,
  volatilityAssumption,
  strategyBuilder,
  strategyMetrics,
  maxProfit,
  maxLoss,
  probabilityOfProfit,
  delta,
  gamma,
  theta,
  vega,
  rho,
  breakEvenPrices,
}: {
  userId: string;
  stockPrice: number;
  stockOptions: StockOptionParamters[];
  underlyingStocks: UnderlyingStockParameters[];
  ticker: string;
  riskFreeRate: number;
  title: string;
  description: string;
  normalizedProfitFactor: number;
  netCreditDebit: number;
  impliedVolatility: number | null;
  volatilityAssumption?: string;
  strategyBuilder: StrategyBuilder;
  strategyMetrics: ReturnType<StrategyBuilder['calculateStrategyMetrics']>;
  maxProfit: number;
  maxLoss: number;
  probabilityOfProfit: number;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  breakEvenPrices: number[];
}): InsertOpcSavedStrategyMutationVariables => {
  const sumOfStockPositions = underlyingStocks.reduce((acc, curr) => acc + curr.quantity, 0);

  const optionContractValue = calculateAggregateContractValueOfOptionPosition(
    strategyBuilder.legs,
    true,
  );
  const { newPositionValue: stockPositionValue } = calculateUnderlyingStockPositionMetrics({
    underlyingStockPositions: underlyingStocks,
    currentPrice: stockPrice,
  });

  const optionWithShortestExpiration = stockOptions.reduce((acc, curr) => {
    if (!acc || curr.expirationDateString < acc.expirationDateString) {
      return curr;
    }
    return acc;
  }, undefined as StockOptionParamters | undefined);

  const expiration = optionWithShortestExpiration?.expirationKey ?? '';

  const strategyValue = optionContractValue + stockPositionValue;

  const positions: NonNullable<
    NonNullable<InsertOpcSavedStrategyMutationVariables['object']>['positions']
  >['data'] = strategyMetrics.strategyLegs.map((leg) => ({
    value: leg.value,
    optionType: leg.type,
    quantity: leg.numberOfContracts,
    strike: leg.strike,
    expiration: leg.expirationDateString,
    premium: leg.premium,
    impliedVolatility: leg.impliedVolatility,
    delta: leg.delta,
    gamma: leg.gamma,
    theta: leg.theta,
    vega: leg.vega,
    rho: leg.rho,
    purchaseDirection: leg.position,
  }));

  return {
    object: {
      userId,
      ticker,
      value: strategyValue,
      stockPrice,
      stockShares: sumOfStockPositions,
      riskFreeRate,
      expiration,
      title,
      description: [
        description,
        volatilityAssumption,
        ...strategyMetrics.strategyLegs.flatMap((leg, index) =>
          leg.impliedVolatilityResult.status === 'unavailable'
            ? [`Leg ${index + 1} IV unavailable: ${leg.impliedVolatilityResult.reason}.`]
            : [],
        ),
      ]
        .filter(Boolean)
        .join('\n'),
      maxProfit,
      maxLoss,
      probabilityOfProfit,
      profitIndex,
      netCreditDebit,
      impliedVolatility,
      delta,
      gamma,
      theta,
      vega,
      rho,
      breakEvenPrices,
      positions: {
        data: positions,
      },
      premiumTransacted: optionContractValue,
    },
  };
};

export const SaveStrategyModal: React.FC<SaveStrategyModalProps> = ({
  isOpen,
  handleClose,
  strategy,
  userId,
  stockPrice,
  stockOptions,
  underlyingStocks,
  ticker,
  riskFreeRate,
  normalizedProfitFactor,
  netCreditDebit,
  impliedVolatility,
  volatilityAssumption,
  strategyBuilder,
  strategyMetrics,
  maxProfit,
  maxLoss,
  probabilityOfProfit,
  delta,
  gamma,
  theta,
  vega,
  rho,
  breakEvenPrices,
}) => {
  const viewer = useViewer();
  const router = useRouter();

  const [strategyName, setStrategyName] = useState<string>(strategy);
  const [notes, setNotes] = useState<string>('');
  const [strategyNameError, setStrategyNameError] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const [insertOpcSavedStrategy, { loading: isLading }] = useInsertOpcSavedStrategyMutation({
    onCompleted: () => {
      setIsSuccess(true);
      setStrategyName('');
      setNotes('');
      setTimeout(() => {
        handleClose();
      }, 500);
    },
  });

  const isLocked = !userId;
  const currentPath = router.asPath;

  const validateStrategyName = (name: string): boolean => {
    if (!name.trim()) {
      setStrategyNameError('Strategy Name is required');
      return false;
    } else {
      setStrategyNameError('');
      return true;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateStrategyName(strategyName)) {
      return;
    }

    setStrategyNameError('');

    const dataToSave = prepareDataForSavingStrategy({
      userId,
      stockPrice,
      stockOptions,
      underlyingStocks,
      ticker,
      riskFreeRate,
      title: strategyName,
      description: notes,
      normalizedProfitFactor,
      netCreditDebit,
      impliedVolatility,
      volatilityAssumption,
      strategyBuilder,
      strategyMetrics,
      maxProfit,
      maxLoss,
      probabilityOfProfit,
      delta,
      gamma,
      theta,
      vega,
      rho,
      breakEvenPrices,
    });

    try {
      await insertOpcSavedStrategy({ variables: dataToSave });
    } catch (error) {
      console.error('Error saving strategy:', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const strikePrices = strategyMetrics?.strategyLegs
        ?.map((leg) => leg.strike)
        ?.sort((a, b) => b - a)
        ?.join('/');

      const contractValue = calculateAggregateContractValueOfOptionPosition(
        strategyBuilder.legs,
        true,
      );
      const totalPremium = `$${contractValue.toFixed(2)}`;

      const nearestExpirationDate = strategyMetrics?.strategyLegs
        ?.map((leg) => leg.expirationDateString)
        ?.filter((dateStr): dateStr is string => !!dateStr)
        ?.sort((a, b) => a.localeCompare(b))?.[0];

      const formattedStrategyName = `${ticker} ${strategy} ${strikePrices} ${totalPremium} ${nearestExpirationDate}`;

      setStrategyName(formattedStrategyName);

      setStrategyNameError('');
      setNotes('');
      setIsSuccess(false);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-lg">
      <div className="relative rounded-lg bg-palette-black-1 p-3">
        <button onClick={() => handleClose()} className="absolute right-3 top-2">
          <XIcon className="w-5" />
        </button>
        <div className="h-full p-3">
          {isLocked ? (
            <div className="flex justify-center">
              <FaLock className="mr-2" />
            </div>
          ) : null}
          {viewer.status !== AuthStatus.User ? (
            <div className="text-center text-lg font-semibold">Saved strategies locked</div>
          ) : (
            <div className="flex justify-center text-4xl font-semibold">Save Strategy</div>
          )}
          <div className="mt-2">
            {viewer.status !== AuthStatus.User ? (
              <>
                <div className="text-pretty text-center text-lg font-normal text-brand-green-primary">
                  Create a free account or
                  <Link href={`${LOGIN_PAGE}?next=${currentPath}`} className="px-1 underline">
                    login
                  </Link>
                  to save your strategy.
                </div>
                <div className="flex justify-center gap-2 py-4">
                  <Link
                    role="button"
                    href={`${SIGNUP_PAGE}?next=${currentPath}`}
                    className="flex items-center justify-center rounded-md  border border-transparent bg-brand-primary px-8 py-2 text-base font-medium text-white no-underline"
                  >
                    <span className="mr-2">Get Free Access</span>
                    <IoArrowRedoOutline />
                  </Link>
                </div>
              </>
            ) : (
              <div className="mt-4">
                {isLading ? (
                  <div className="flex w-full justify-center">
                    <PulseLoader />
                  </div>
                ) : isSuccess ? (
                  <div className="mt-4 text-center text-brand-green-primary">
                    Strategy saved successfully!
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {volatilityAssumption && (
                      <p className="text-xs text-amber-200">
                        {volatilityAssumption} This assumption will be included in the saved notes.
                      </p>
                    )}
                    <div>
                      <label
                        htmlFor="Strategy"
                        className="block text-sm font-medium leading-6 text-white"
                      >
                        Strategy Name
                      </label>
                      <div className="mt-2">
                        <input
                          id="strategyName"
                          name="strategyName"
                          type="text"
                          value={strategyName}
                          onChange={(e) => {
                            setStrategyName(e.target.value);
                            if (strategyNameError) validateStrategyName(e.target.value);
                          }}
                          onBlur={() => validateStrategyName(strategyName)}
                          placeholder="Enter strategy name"
                          className="mb-2.5 block h-12 w-full rounded border-2 border-transparent bg-palette-black-4 p-2.5 px-4 text-sm font-medium leading-relaxed text-white transition-colors duration-300 hover:border-brand-primary focus:border-brand-primary focus:outline-none"
                        />
                        {strategyNameError && (
                          <p className="mt-1 text-sm text-red-500">{strategyNameError}</p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="Notes"
                        className="block text-sm font-medium leading-6 text-white"
                      >
                        Strategy Notes
                      </label>
                      <div className="mt-2">
                        <textarea
                          id="description"
                          name="description"
                          rows={3}
                          placeholder="Optional: Enter any trade notes or research here"
                          className="mb-2.5 block w-full rounded border-2 border-transparent bg-palette-black-4 p-2.5 px-4 text-sm font-medium leading-relaxed text-white transition-colors duration-300 hover:border-brand-primary focus:border-brand-primary focus:outline-none"
                          onChange={(e) => setNotes(e.target.value)}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="flex w-full items-center justify-center rounded-md bg-brand-primary px-2.5 py-3 text-sm  font-medium ring-1 ring-inset ring-palette-black-1"
                    >
                      Save Strategy
                    </button>
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SaveStrategyModal;
